import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Boxes, FileStack, Calendar } from 'lucide-react';
import { payoutAPI } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

/**
 * FinancialOperations — second Command Center band on the Accounting
 * dashboard. Surfaces OPERATIONAL structure — how many ledger rows, how
 * many import batches, how many statement months are on file. Deliberately
 * NO $ totals, revenue trends, or performance rankings (Phase 20.7).
 *
 * Data source (pre-existing):
 *   GET /payout/ledger-stats  →  { total_entries, import_batch_count,
 *                                  available_months, by_status, by_type }
 *
 * If the ledger has zero entries (a fresh Accounting deployment or an
 * agency without ledger imports), we show a professional empty state
 * instead of a wall of zeros. This is the "invented numbers" guardrail
 * from the phase spec.
 */

const Metric = ({ label, value, hint, icon: Icon, testId }) => (
  <div className="flex items-start gap-3" data-testid={testId}>
    {Icon && (
      <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 bg-slate-100 text-slate-600">
        <Icon size={17} />
      </div>
    )}
    <div className="min-w-0">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
        {label}
      </div>
      <div className="text-lg font-semibold mt-0.5 text-slate-900 truncate tabular-nums">
        {value ?? '—'}
      </div>
      {hint && <div className="text-[11px] text-slate-500 mt-0.5">{hint}</div>}
    </div>
  </div>
);

export const FinancialOperations = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await payoutAPI.ledger.stats();
        if (cancelled) return;
        setStats(res?.data || null);
      } catch (e) {
        if (cancelled) return;
        console.error('Failed to load financial operations stats:', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const totalEntries = stats?.total_entries ?? 0;
  const batchCount = stats?.import_batch_count ?? 0;
  const monthCount = Array.isArray(stats?.available_months) ? stats.available_months.length : 0;
  const isEmpty = !loading && totalEntries === 0 && batchCount === 0 && monthCount === 0;
  const latestMonth = Array.isArray(stats?.available_months) && stats.available_months.length > 0
    ? stats.available_months[0]
    : null;

  return (
    <section className="mb-8" data-testid="financial-operations">
      <div className="mb-3">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500">
          Financial Operations
        </h2>
        <p className="text-sm text-slate-500 mt-1">
          Operational shape of the ledger — how much data is on file and where
          the most recent activity lives.
        </p>
      </div>

      <Card className="animate-slideIn">
        <CardContent className="p-5">
          {loading ? (
            <div className="py-6 text-center text-sm text-slate-400" data-testid="financial-operations-loading">
              Loading financial operations…
            </div>
          ) : isEmpty ? (
            <div className="py-6 text-center" data-testid="financial-operations-empty">
              <p className="text-sm font-medium text-slate-800">No ledger data yet</p>
              <p className="text-xs text-slate-500 mt-1 mb-4">
                Once your first commission or override import lands, this
                section will summarize what&apos;s on file.
              </p>
              <Link to="/accounting/compensation">
                <Button variant="outline" size="sm" data-testid="financial-operations-open-compensation">
                  Open Compensation Center
                </Button>
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              <Metric
                label="Total Ledger Entries"
                value={totalEntries.toLocaleString()}
                icon={Boxes}
                hint="Source: /payout/ledger-stats (count of ledger rows on file)"
                testId="financial-operations-total-entries"
              />
              <Metric
                label="Import Batches"
                value={batchCount.toLocaleString()}
                icon={FileStack}
                hint="Source: /payout/ledger-stats (distinct import_batch_id)"
                testId="financial-operations-import-batches"
              />
              <Metric
                label="Statement Months on File"
                value={monthCount.toLocaleString()}
                icon={Calendar}
                hint={latestMonth ? `Most recent: ${latestMonth}` : 'Source: /payout/ledger-stats (distinct statement_month)'}
                testId="financial-operations-statement-months"
              />
            </div>
          )}
        </CardContent>
      </Card>
    </section>
  );
};

export default FinancialOperations;
